import { AlignCenter, AlignLeft, AlignRight, EyeOff } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import type { UseFormReturn } from 'react-hook-form'
import { FormControl, FormItem, FormMessage } from '@/components/ui/form'
import InputFieldContainer from '@/components/shared/inputComponents/InputFieldContainer'
import { cn } from '@/lib/utils'
import { ContentBlockFormValues } from '../types'

type TitleAlignment = 'left' | 'center' | 'right'

interface TitleAlignmentControlProps {
  form: UseFormReturn<ContentBlockFormValues>
}

export function TitleAlignmentControl({ form }: TitleAlignmentControlProps) {
  const titleAlignment = form.watch('titleAlignment')
  const hideTitle = form.watch('hideTitle')

  const active = hideTitle ? 'hide' : (titleAlignment ?? 'left')

  const setAlignment = (value: TitleAlignment) => {
    form.setValue('titleAlignment', value, { shouldDirty: true })
    form.setValue('hideTitle', false, { shouldDirty: true })
  }

  const hide = () => {
    form.setValue('hideTitle', true, { shouldDirty: true })
    form.setValue('titleAlignment', form.getValues('titleAlignment'), {
      shouldDirty: true,
    })
  }

  const options: {
    key: string
    label: string
    icon: LucideIcon
    onClick: () => void
  }[] = [
    {
      key: 'left',
      label: 'Align left',
      icon: AlignLeft,
      onClick: () => setAlignment('left'),
    },
    {
      key: 'center',
      label: 'Align center',
      icon: AlignCenter,
      onClick: () => setAlignment('center'),
    },
    {
      key: 'right',
      label: 'Align right',
      icon: AlignRight,
      onClick: () => setAlignment('right'),
    },
    {
      key: 'hide',
      label: 'Hide title',
      icon: EyeOff,
      onClick: hide,
    },
  ]

  return (
    <InputFieldContainer
      label="Title Alignment"
      control={form.control}
      name="titleAlignment"
      render={() => (
        <FormItem className="col-span-3">
          <FormControl>
            <div className="inline-flex overflow-hidden rounded-md border border-input bg-background">
              {options.map((option, index) => {
                const Icon = option.icon
                const isActive = active === option.key
                return (
                  <button
                    key={option.key}
                    type="button"
                    aria-label={option.label}
                    title={option.label}
                    onClick={option.onClick}
                    className={cn(
                      'p-2 transition-colors',
                      index > 0 && 'border-l border-input',
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                    )}
                  >
                    <Icon className="h-4 w-4" />
                  </button>
                )
              })}
            </div>
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}
